import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import {
  BookkeepingQuestion,
  SubmitQuestionResponse,
} from "@/types/bookkeepingQa";

export class BookkeepingQaApiClient {
  /**
   * Submit a bookkeeping question (queues a background job)
   */
  async submitQuestion(questionText: string): Promise<SubmitQuestionResponse> {
    try {
      console.log('BookkeepingQaApiClient: Submitting question');
      const { data, error } = await supabase.functions.invoke('bookkeeping-qa-api', {
        body: {
          action: 'submitQuestion',
          questionText
        }
      });

      if (error) {
        console.error('Error submitting bookkeeping question:', error);
        throw error;
      }

      console.log('BookkeepingQaApiClient: Question queued:', data);
      return data as SubmitQuestionResponse;
    } catch (error) {
      console.error('Exception submitting bookkeeping question:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * Get a specific bookkeeping question by ID
   */
  async getQuestion(questionId: string): Promise<BookkeepingQuestion> {
    try {
      console.log(`BookkeepingQaApiClient: Getting question ${questionId}`);
      const { data, error } = await supabase.functions.invoke('bookkeeping-qa-api', {
        body: {
          action: 'getQuestion',
          questionId
        }
      });

      if (error) {
        console.error(`Error getting bookkeeping question ${questionId}:`, error);
        throw error;
      }

      return data.question as BookkeepingQuestion;
    } catch (error) {
      console.error(`Exception getting bookkeeping question ${questionId}:`, error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }

  /**
   * List bookkeeping questions with optional pagination
   */
  async listQuestions(
    pagination?: { limit: number; offset: number }
  ): Promise<{ questions: BookkeepingQuestion[]; total: number }> {
    try {
      console.log('BookkeepingQaApiClient: Listing questions');
      const { data, error } = await supabase.functions.invoke('bookkeeping-qa-api', {
        body: {
          action: 'listQuestions',
          pagination: pagination ?? { limit: 50, offset: 0 }
        }
      });

      if (error) {
        console.error('Error listing bookkeeping questions:', error);
        throw error;
      }

      console.log(`BookkeepingQaApiClient: Received ${data.questions?.length || 0} questions`);
      return data as { questions: BookkeepingQuestion[]; total: number };
    } catch (error) {
      console.error('Exception listing bookkeeping questions:', error);

      throw await toEdgeFunctionFailure(error, 'Unknown error');
    }
  }
}
